import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight, FiAlertCircle } = FiIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  const {
    signIn,
    isAuthenticated,
    loading,
    clearError,
    error: authError,
    profile,
    profileLoading,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);

  // Get the redirect path from location state, or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';
  const shouldRespectFrom = from !== '/dashboard';
  const roleRedirectMap = {
    agent: '/agent/dashboard',
    student: '/student/dashboard',
    admin: '/admin/dashboard',
  };
  const resolvedRole = profile?.role ?? null;
  const roleRedirectPath = roleRedirectMap[resolvedRole] || '/dashboard';
  const redirectTarget = shouldRespectFrom ? from : roleRedirectPath;
  const canRedirect = Boolean(resolvedRole) || (profileChecked && !profileLoading);

  // Clear errors when component mounts or inputs change
  useEffect(() => {
    setFormError('');
    clearError?.();
  }, [email, password, clearError]);

  useEffect(() => {
    if (profileLoading || profile || resolvedRole) {
      setProfileChecked(true);
    }
  }, [profileLoading, profile, resolvedRole]);

  useEffect(() => {
    if (!redirectAfterLogin || !isAuthenticated || !canRedirect) {
      return;
    }

    navigate(redirectTarget, { replace: true });
    setRedirectAfterLogin(false);
  }, [redirectAfterLogin, isAuthenticated, canRedirect, navigate, redirectTarget]);

  // Redirect if already authenticated
  if (!loading && isAuthenticated) {
    if (!canRedirect) {
      return null;
    }
    return <Navigate to={redirectTarget} replace />;
  }

  const validateForm = () => {
    if (!email.trim()) {
      setFormError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Please enter a valid email address');
      return false;
    }
    if (!password) {
      setFormError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setProfileChecked(false);

    try {
      const result = await signIn(email, password);

      if (result.success) {
        toast.success('Welcome back!');
        setRedirectAfterLogin(true);
      } else {
        const errorMessage = result.error || 'Login failed. Please try again.';
        setFormError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred';
      setFormError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayError = formError || authError;

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#00AAFF] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-400 rounded-full blur-3xl"></div>
      </div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMC41Ii8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-5"></div>

      <div className="relative z-10 w-full max-w-md px-4">
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-flex items-center space-x-3 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-[#00AAFF] to-emerald-400 rounded-xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-2xl">PL</span>
            </div>
            <div className="text-left">
              <span className="text-2xl font-bold text-white block leading-none" style={{ fontFamily: 'Dancing Script, cursive' }}>
                Prosperity Leaders
              </span>
              <span className="text-xs text-slate-400">Events Platform</span>
            </div>
          </Link>
        </motion.div>

        {/* Login Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Welcome Back</h2>
            <p className="text-slate-300">Sign in to access your dashboard</p>
          </div>

          {/* Error Alert */}
          {displayError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border border-red-400/20 rounded-xl p-4 mb-6 flex items-start space-x-3"
            >
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{displayError}</p>
            </motion.div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2">
                Email Address
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  className="pl-12 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent text-white placeholder-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  placeholder="Enter your email"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-slate-200">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-[#00AAFF] hover:text-[#00AAFF]/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  className="pl-12 pr-12 w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent text-white placeholder-slate-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="group w-full bg-gradient-to-r from-[#00AAFF] to-[#0088dd] text-white py-3.5 px-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-[#00AAFF]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center space-y-4">
            <p className="text-slate-300 text-sm">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-[#00AAFF] hover:text-[#00AAFF]/80 font-medium transition-colors"
              >
                Sign up
              </Link>
            </p>
            <Link to="/" className="text-sm text-slate-400 hover:text-white transition-colors inline-block">
              &larr; Back to Home
            </Link>
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center text-slate-400 text-sm mt-6"
        >
          Secure login powered by Supabase
        </motion.p>
      </div>
    </div>
  );
};

export default Login;
